/* iter209 — "Where Their Hours Went" allocation breakdown.
   Read-only for employees; admin mode adds correction tools (edit / split / void /
   add / reassign) — all reason-gated with a before/after audit on the backend. */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { fmtETTime } from '../utils/tz';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Truck, Wrench, Warehouse, Building2, Coffee, HelpCircle, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const SEG_TYPES = [
  ['travel_en_route', 'Travel / En Route'],
  ['on_site', 'On-Site Work'],
  ['shop_warehouse', 'Shop / Warehouse'],
  ['office_admin', 'Office / Admin'],
  ['available_general', 'Available / General'],
];

const fmtHM = (m) => { m = Math.max(0, Math.round(Number(m) || 0)); const h = Math.floor(m / 60), r = m % 60; return h && r ? `${h}h ${r}m` : h ? `${h}h` : `${r}m`; };
const fmtT = (iso) => iso ? fmtETTime(iso) : '—';
const toLocalInput = (iso) => { if (!iso) return ''; const d = new Date(iso); const off = d.getTimezoneOffset(); const l = new Date(d.getTime() - off * 60000); return l.toISOString().slice(0, 16); };

const typeIcon = (t) => ({ travel_en_route: Truck, on_site: Wrench, shop_warehouse: Warehouse, office_admin: Building2, available_general: Clock, unallocated: HelpCircle, historical: Clock }[t] || Clock);
const typeColor = (t) => ({ travel_en_route: 'text-sky-300', on_site: 'text-[#D4AF37]', shop_warehouse: 'text-emerald-300', office_admin: 'text-violet-300', available_general: 'text-[#A3A3A3]', unallocated: 'text-red-400', historical: 'text-[#737373]' }[t] || 'text-white');

const STATUS_MSG = {
  balanced: { c: 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10', t: 'Reconciled — allocation equals paid time' },
  unallocated: { c: 'text-amber-300 border-amber-500/40 bg-amber-500/10', t: 'Unallocated time' },
  overlapping: { c: 'text-red-300 border-red-500/40 bg-red-500/10', t: 'Overlapping segments' },
  allocation_exceeds_paid: { c: 'text-red-300 border-red-500/40 bg-red-500/10', t: 'Allocation exceeds paid time' },
  open_segment: { c: 'text-sky-300 border-sky-500/40 bg-sky-500/10', t: 'Open segment (still on clock)' },
};

const TimeAllocation = ({ allocation, userId, date, adminMode = false, onChanged }) => {
  const [editRow, setEditRow] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [splitRow, setSplitRow] = useState(null);
  const [mergeCtx, setMergeCtx] = useState(null);
  if (!allocation) return null;
  const s = allocation.summary || {};
  const rows = allocation.rows || [];
  const st = STATUS_MSG[s.status] || STATUS_MSG.balanced;

  return (
    <div data-testid="time-allocation" className="bg-[#141414] border border-[#262626] rounded-md p-4">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <h3 className="font-['Outfit'] text-sm font-bold text-white flex items-center gap-2">
          <Wrench className="w-4 h-4 text-[#D4AF37]" /> Where Their Hours Went
          <span className="text-[10px] text-[#737373] font-normal normal-case">(America/New_York)</span>
        </h3>
        {adminMode && (
          <Button size="sm" variant="outline" data-testid="alloc-add" onClick={() => setAddOpen(true)} className="border-[#D4AF37]/50 text-[#D4AF37] text-xs">+ Add Segment</Button>
        )}
      </div>

      <div data-testid="alloc-status" className={`text-[11px] font-semibold border rounded px-2 py-1 inline-block mb-3 ${st.c}`}>
        {st.t}
        {s.unallocated_minutes > 0 && s.status === 'unallocated' ? ` · ${fmtHM(s.unallocated_minutes)}` : ''}
        {s.over_allocated_minutes > 0 && s.status === 'allocation_exceeds_paid' ? ` · +${fmtHM(s.over_allocated_minutes)}` : ''}
      </div>

      {rows.length === 0 ? (
        <p className="text-xs text-[#737373]">No allocation recorded for this day.</p>
      ) : (
        <div className="space-y-2">
          {rows.map((r, i) => {
            const Icon = typeIcon(r.type);
            return (
              <div key={r.id || i} data-testid={`alloc-row-${i}`} className={`flex items-start gap-3 rounded-md border p-2.5 ${r.type === 'unallocated' ? 'border-red-500/30 bg-red-500/5' : r.type === 'historical' ? 'border-[#262626] bg-[#0d0d0d]' : 'border-[#262626]'}`}>
                <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${typeColor(r.type)}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className={`text-sm font-medium ${typeColor(r.type)}`}>
                      {r.type_label}{r.is_helper ? ' · Helper' : ''}
                    </p>
                    <p className="text-sm font-bold text-white">{fmtHM(r.minutes)}{r.active ? ' · active' : ''}</p>
                  </div>
                  <p className="text-xs text-[#A3A3A3]">
                    {r.start ? `${fmtT(r.start)} → ${r.active ? 'now' : fmtT(r.end)}` : (r.note || '')}
                  </p>
                  {(r.customer_name || r.activity_label || r.work_order_number) && (
                    <p className="text-xs text-[#D4D4D4] truncate">
                      {r.customer_name || r.activity_label}{r.work_order_number ? ` · WO ${r.work_order_number}` : ''}
                    </p>
                  )}
                  {adminMode && r.id && (
                    <div className="mt-1.5 flex gap-2 flex-wrap">
                      <button data-testid={`alloc-edit-${i}`} onClick={() => setEditRow(r)} className="text-[10px] uppercase tracking-wider text-[#D4AF37] hover:underline">Correct</button>
                      <button data-testid={`alloc-split-${i}`} onClick={() => setSplitRow(r)} className="text-[10px] uppercase tracking-wider text-sky-300 hover:underline">Split</button>
                      {(() => {
                        const next = rows.slice(i + 1).find((x) => x.id);
                        if (next && next.type === r.type) {
                          return <button data-testid={`alloc-merge-${i}`} onClick={() => setMergeCtx({ row: r, otherId: next.id, otherLabel: next.type_label })} className="text-[10px] uppercase tracking-wider text-emerald-300 hover:underline">Merge ↓</button>;
                        }
                        return null;
                      })()}
                      <button data-testid={`alloc-void-${i}`} onClick={() => setEditRow({ ...r, _void: true })} className="text-[10px] uppercase tracking-wider text-red-400 hover:underline">Void</button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-3 pt-3 border-t border-[#262626] grid grid-cols-3 gap-2 text-center">
        <div><p className="text-[10px] uppercase text-[#A3A3A3]">Allocated</p><p className="text-sm font-bold text-white">{fmtHM(s.allocated_minutes)}</p></div>
        <div><p className="text-[10px] uppercase text-[#A3A3A3]">Paid</p><p className="text-sm font-bold text-[#D4AF37]">{fmtHM(s.paid_minutes)}</p></div>
        <div><p className="text-[10px] uppercase text-[#A3A3A3]">Unallocated</p><p className={`text-sm font-bold ${s.unallocated_minutes > 0 ? 'text-amber-300' : 'text-white'}`}>{fmtHM(s.unallocated_minutes)}</p></div>
      </div>

      {adminMode && <SegmentEditModal row={editRow} onClose={() => setEditRow(null)} onDone={() => { setEditRow(null); onChanged && onChanged(); }} paid={s.paid_minutes} allocated={s.allocated_minutes} />}
      {adminMode && <SegmentAddModal open={addOpen} userId={userId} date={date} onClose={() => setAddOpen(false)} onDone={() => { setAddOpen(false); onChanged && onChanged(); }} />}
      {adminMode && <SegmentSplitModal row={splitRow} onClose={() => setSplitRow(null)} onDone={() => { setSplitRow(null); onChanged && onChanged(); }} />}
      {adminMode && <SegmentMergeModal ctx={mergeCtx} onClose={() => setMergeCtx(null)} onDone={() => { setMergeCtx(null); onChanged && onChanged(); }} />}
    </div>
  );
};

const SegmentSplitModal = ({ row, onClose, onDone }) => {
  const [at, setAt] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  React.useEffect(() => { if (row) { setAt(toLocalInput(row.start)); setReason(''); } }, [row]);
  if (!row) return null;
  const submit = async () => {
    if (!at) { toast.error('Pick a split time'); return; }
    if (!reason.trim()) { toast.error('Reason is required'); return; }
    const atIso = new Date(at).toISOString();
    if (row.start && new Date(atIso) <= new Date(row.start)) { toast.error('Split time must be after the segment start'); return; }
    if (row.end && new Date(atIso) >= new Date(row.end)) { toast.error('Split time must be before the segment end'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/timesheets/segments/${row.id}/split`, { at: atIso, reason: reason.trim() }, { withCredentials: true });
      toast.success('Segment split');
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Split failed'); }
    setBusy(false);
  };
  return (
    <Dialog open={!!row} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="alloc-split-dialog" className="bg-[#141414] border-[#262626] text-white max-w-sm">
        <DialogHeader><DialogTitle className="text-sky-300">Split Segment</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-[#A3A3A3] text-xs">{row.type_label} · {fmtT(row.start)} → {fmtT(row.end)}. The segment splits into two at the chosen time.</p>
          <div><label className="text-[11px] uppercase text-[#A3A3A3]">Split at</label><Input type="datetime-local" data-testid="alloc-split-at" value={at} onChange={(e) => setAt(e.target.value)} className="bg-[#0A0A0A] border-[#262626] mt-1" /></div>
          <div><label className="text-[11px] uppercase text-[#A3A3A3]">Reason (required)</label><Textarea data-testid="alloc-split-reason" value={reason} onChange={(e) => setReason(e.target.value)} className="bg-[#0A0A0A] border-[#262626] mt-1" rows={2} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-[#262626] text-white">Cancel</Button>
          <Button data-testid="alloc-split-submit" onClick={submit} disabled={busy || !reason.trim()} className="bg-sky-500 text-[#0A0A0A] font-bold disabled:opacity-40">{busy ? 'Splitting…' : 'Split'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SegmentMergeModal = ({ ctx, onClose, onDone }) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  React.useEffect(() => { if (ctx) setReason(''); }, [ctx]);
  if (!ctx) return null;
  const submit = async () => {
    if (!reason.trim()) { toast.error('Reason is required'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/timesheets/segments/${ctx.row.id}/merge`, { other_id: ctx.otherId, reason: reason.trim() }, { withCredentials: true });
      toast.success('Segments merged');
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Merge failed'); }
    setBusy(false);
  };
  return (
    <Dialog open={!!ctx} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="alloc-merge-dialog" className="bg-[#141414] border-[#262626] text-white max-w-sm">
        <DialogHeader><DialogTitle className="text-emerald-300">Merge Segments</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-[#A3A3A3] text-xs">Merge this <span className="text-white font-bold">{ctx.row.type_label}</span> segment with the adjacent one. Both must be the same type. This cannot be undone (re-split if needed).</p>
          <div><label className="text-[11px] uppercase text-[#A3A3A3]">Reason (required)</label><Textarea data-testid="alloc-merge-reason" value={reason} onChange={(e) => setReason(e.target.value)} className="bg-[#0A0A0A] border-[#262626] mt-1" rows={2} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-[#262626] text-white">Cancel</Button>
          <Button data-testid="alloc-merge-submit" onClick={submit} disabled={busy || !reason.trim()} className="bg-emerald-500 text-[#0A0A0A] font-bold disabled:opacity-40">{busy ? 'Merging…' : 'Merge'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SegmentEditModal = ({ row, onClose, onDone, paid, allocated }) => {
  const [type, setType] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  React.useEffect(() => { if (row) { setType(row.type || ''); setStart(toLocalInput(row.start)); setEnd(toLocalInput(row.end)); setReason(''); } }, [row]);
  if (!row) return null;
  const isVoid = row._void;

  const submit = async () => {
    if (!reason.trim()) { toast.error('A correction reason is required.'); return; }
    setBusy(true);
    try {
      if (isVoid) {
        await axios.post(`${API}/admin/timesheets/segments/${row.id}/void`, { reason: reason.trim() }, { withCredentials: true });
        toast.success('Segment voided');
      } else {
        await axios.put(`${API}/admin/timesheets/segments/${row.id}`, {
          type, start: start ? new Date(start).toISOString() : null, end: end ? new Date(end).toISOString() : null, reason: reason.trim(),
        }, { withCredentials: true });
        toast.success('Segment corrected');
      }
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Correction failed'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!row} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#141414] border border-[#262626] text-white" data-testid="alloc-edit-modal">
        <DialogHeader><DialogTitle>{isVoid ? 'Void Segment' : 'Correct Segment'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {!isVoid && (<>
            <div>
              <label className="text-[11px] uppercase text-[#A3A3A3]">Type</label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger data-testid="alloc-edit-type" className="bg-[#0A0A0A] border-[#262626] mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626] text-white">
                  {SEG_TYPES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-[11px] uppercase text-[#A3A3A3]">Start</label><Input type="datetime-local" data-testid="alloc-edit-start" value={start} onChange={(e) => setStart(e.target.value)} className="bg-[#0A0A0A] border-[#262626] mt-1" /></div>
              <div><label className="text-[11px] uppercase text-[#A3A3A3]">End</label><Input type="datetime-local" data-testid="alloc-edit-end" value={end} onChange={(e) => setEnd(e.target.value)} className="bg-[#0A0A0A] border-[#262626] mt-1" /></div>
            </div>
            <p className="text-[11px] text-[#737373]">Paid {fmtHM(paid)} · currently allocated {fmtHM(allocated)}. Segments cannot overlap or exceed the clock window.</p>
          </>)}
          <div>
            <label className="text-[11px] uppercase text-[#A3A3A3]">Reason (required)</label>
            <Textarea data-testid="alloc-edit-reason" value={reason} onChange={(e) => setReason(e.target.value)} className="bg-[#0A0A0A] border-[#262626] mt-1" rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-[#262626] text-[#A3A3A3]">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="alloc-edit-save" className={isVoid ? 'bg-red-600 text-white hover:bg-red-500' : 'bg-[#D4AF37] text-black hover:bg-[#c5a233]'}>{isVoid ? 'Void' : 'Save Correction'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SegmentAddModal = ({ open, userId, date, onClose, onDone }) => {
  const [type, setType] = useState('available_general');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  React.useEffect(() => { if (open) { setType('available_general'); setStart(''); setEnd(''); setReason(''); } }, [open]);
  if (!open) return null;

  const submit = async () => {
    if (!reason.trim()) { toast.error('A reason is required.'); return; }
    if (!start || !end) { toast.error('Start and end are required.'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/timesheets/segments`, {
        user_id: userId, date, type, start: new Date(start).toISOString(), end: new Date(end).toISOString(), reason: reason.trim(),
      }, { withCredentials: true });
      toast.success('Segment added'); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Add failed'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#141414] border border-[#262626] text-white" data-testid="alloc-add-modal">
        <DialogHeader><DialogTitle>Add Segment</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[11px] uppercase text-[#A3A3A3]">Type</label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="alloc-add-type" className="bg-[#0A0A0A] border-[#262626] mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#141414] border-[#262626] text-white">{SEG_TYPES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-[11px] uppercase text-[#A3A3A3]">Start</label><Input type="datetime-local" data-testid="alloc-add-start" value={start} onChange={(e) => setStart(e.target.value)} className="bg-[#0A0A0A] border-[#262626] mt-1" /></div>
            <div><label className="text-[11px] uppercase text-[#A3A3A3]">End</label><Input type="datetime-local" data-testid="alloc-add-end" value={end} onChange={(e) => setEnd(e.target.value)} className="bg-[#0A0A0A] border-[#262626] mt-1" /></div>
          </div>
          <div><label className="text-[11px] uppercase text-[#A3A3A3]">Reason (required)</label><Textarea data-testid="alloc-add-reason" value={reason} onChange={(e) => setReason(e.target.value)} className="bg-[#0A0A0A] border-[#262626] mt-1" rows={2} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-[#262626] text-[#A3A3A3]">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="alloc-add-save" className="bg-[#D4AF37] text-black hover:bg-[#c5a233]">Add Segment</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TimeAllocation;
